/**
 * @fileOverview ViewStore for shortcut handling date picker from React
 *					component.
 * @author jzetlen
 *
 */

import invariant from 'invariant-slim';
import { findKey, every, zip } from 'underscore';
import moment from 'moment';
import trackLink from 'view/activity/trackLink';
import presets from 'widgets/datepickerPresets';

/**
 * @function shortcutHandling
 * Factory for a shortcut handling date picker store. This is a View Store
 * function; for more information on the signature of these functions,
 * see stores/base.es6.
 */
export default function shortcutHandling({ customShortcuts }) {
	// this outer closure is for utilities and state that will be common
	// to all stores, similar to static properties on a class.
	invariant(
		customShortcuts.toArray && customShortcuts.jquery,
		'config.customShortcuts must be a jQuery object'
	);
	return function({ getState, updateState, onUpdate, publish }) {
		// this inner closure is for state that will be private to the
		// individual store created with each invocation of this function,
		// similar to private instance properties on a class.
		return {
			// shortcuts should be an array of { label, id, className } objects
			// with `label` as the text label of the link, `id` as a unique
			// id that signals to us what shortcut to use, and `className a CSS
			// classname to apply to the link.
			shortcuts: getShortcutData(customShortcuts),
			// the Shortcuts component passes the `id` of the clicked shortcut
			// back as the second argument. it will be one of the functions
			// in `widgets/datepickerPresets`, so we just publish the dates
			// returned by that preset.
			onClickShortcut: (event, presetName) => {
				event.preventDefault();
				event.stopPropagation();
				const { startDate, endDate } = getState();
				if (getActiveShortcut(startDate, endDate) !== presetName) {
					trackLink(`opencalendar:${presetName}`);
					publish(event, ...presets[presetName]());
				}
			},
			getActiveShortcut: () => {
				const { startDate, endDate } = getState();
				return getActiveShortcut(startDate, endDate);
			}
		};
	};
}

/**
 * Expects two arrays of dates and returns whether they are equivalent ranges,
 * to a precision of one day.
 */
function dateRangesEqual(range1, range2) {
	return every(
		zip(range1, range2),
		([date1, date2]) => moment(date1).isSame(date2, 'day')
	);
}

/**
 * Takes a key-value pair of "date range shortcut" methods that generate a
 * tuple of start/end dates such as "lastmonth" or "ytd", and a "state" object
 * with startDate and endDate properties.
 * Returns the key in the first object, such as "ytd", whose values equal the
 * startDate and the endDate in the second object. This tells you which
 * "shortcut" method in the first object is currently "active" in the second.
 */
export function getActiveShortcut(startDate, endDate) {
	return findKey(
		presets,
		(shortcut) => dateRangesEqual(shortcut(), [startDate, endDate])
	);
}

/**
 * Takes a jQuery collection of buttons or links that refer to "date shortcuts"
 * (that must have a `data-preset` attribute),
 * and translates them into an array of { label, id, className } tuples, for
 * use generating React views or other metadata.
 * Using this function allows us to derive the available shortcuts from another
 * part of the HTML view.
 */
export function getShortcutData($el) {
	return $el.toArray().map((button, i) => ({
		label: button.textContent || button.innerText,
		id: button.getAttribute('data-preset'),
		className: 'nemo_datePickerPreset' + i
	}));
}



// WEBPACK FOOTER //
// ./view/activity/dateRangePicker/stores/shortcutHandling.es6